import React, { useMemo, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import ContractsContext from 'contexts/ContractsContext';
import AssessmentsContext from 'contexts/AssessmentsContext';

// assets
import { IconChevronRight, IconWritingSign } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { container } = useConfig();

    const { contracts } = useContext(ContractsContext);
    const { assessmentsEnabled } = useContext(AssessmentsContext);

    React.useEffect(() => {
        dispatch(openDrawer(!matchDownMd));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const header = useMemo(
        () => (
            <Toolbar>
                <Header />
            </Toolbar>
        ),
        []
    );

    const dynamicNavigation = useMemo(() => {
        const nav = {
            ...navigation,
            items: navigation.items.map((item) => {
                if (item.id === 'other') {
                    const otherItem = { ...item };
                    otherItem.children = [...item.children];

                    if (!localStorage.roles?.includes('Admin') && contracts?.length) {
                        const contractsExists = otherItem.children.some((child) => child.id === 'contracts');

                        if (!contractsExists) {
                            otherItem.children.push({
                                id: 'contracts',
                                title: <FormattedMessage id="Contracts" />,
                                type: 'item',
                                url: '/contracts',
                                icon: IconWritingSign,
                                children: [
                                    {
                                        id: 'signContract',
                                        title: <FormattedMessage id="Sign Contract" />,
                                        type: 'item',
                                        url: '/contracts/:sessionId',
                                        icon: IconWritingSign
                                    }
                                ]
                            });
                        }
                    } else {
                        otherItem.children = otherItem.children.filter((child) => child.id !== 'contracts');
                    }

                    // None of the user's groups is at a school with assessments on
                    if (!assessmentsEnabled) {
                        otherItem.children = otherItem.children.filter((child) => child.id !== 'assessments');
                    }

                    return otherItem;
                }
                return item;
            })
        };

        return nav;
    }, [contracts, assessmentsEnabled]);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                {header}
            </AppBar>

            {/* drawer */}
            <Sidebar navigation={dynamicNavigation} />

            {/* main content */}
            <Main theme={theme} open={drawerOpen}>
                {/* breadcrumb */}
                {container && (
                    <Container maxWidth="lg">
                        <Breadcrumbs separator={IconChevronRight} navigation={dynamicNavigation} icon title rightAlign />
                        <Outlet />
                    </Container>
                )}
                {!container && (
                    <>
                        <Breadcrumbs separator={IconChevronRight} navigation={dynamicNavigation} icon title rightAlign />
                        <Outlet />
                    </>
                )}
            </Main>
        </Box>
    );
};

export default MainLayout;
